import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Select,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { GLASS, GLASS_FORM, GLASS_INTERACTIVE } from '@/constants/design-tokens';
import { stockTransfersApi, useCreateTransfer } from '@/features/stock-transfers';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { locationService } from '@/services/location.service';
import { productService } from '@/services/product.service';
import { ingredientService } from '@/services/ingredient.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { buildProductSearchParams } from './stock-transfer-products';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, ArrowRightIcon, PlusIcon, TrashIcon } from './icons';

// ─── Item row interface ─────────────────────────────────────────────────────

interface ItemRow {
  key: string;
  product_id: string;
  product_name: string;
  product_sku: string;
  available_stock: number;
  quantity: string;
}

function generateKey() {
  return Math.random().toString(36).slice(2, 10);
}

// ─── Content ────────────────────────────────────────────────────────────────

function StockTransferCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const createMutation = useCreateTransfer();

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [sourceLocationId, setSourceLocationId] = useState(selectedLocationId ?? '');
  const [destinationLocationId, setDestinationLocationId] = useState('');
  const [notes, setNotes] = useState('');

  const [items, setItems] = useState<ItemRow[]>([
    { key: generateKey(), product_id: '', product_name: '', product_sku: '', available_stock: 0, quantity: '' },
  ]);
  const [productSearch, setProductSearch] = useState('');

  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locations = locationsData?.data ?? [];
  // Destinations are every active tenant location: only the source must be one
  // of the caller's (QA 2.6.4), so the source list above stays user-scoped.
  const { data: destinationsData } = useQuery({
    queryKey: ['transfer-destinations'],
    queryFn: () => stockTransfersApi.destinations(),
    staleTime: 60_000,
  });

  // Restaurants move INGREDIENTS between kitchens (Phase 5); retail moves
  // products. Rows still key off `product_id` — for a restaurant it holds the
  // ingredient id and the payload maps it to `ingredient_id`.
  const businessType = useAuthStore((st) => st.profile?.tenant?.business_type);
  const isRestaurant = businessType === 'RESTAURANT';

  const { data: productsData } = useQuery({
    queryKey: ['products-search', productSearch, sourceLocationId],
    queryFn: () => productService.list(buildProductSearchParams(productSearch, sourceLocationId)),
    staleTime: 15_000,
    enabled: !isRestaurant,
  });
  const { data: ingredientsData } = useQuery({
    queryKey: ['ingredients-search', sourceLocationId],
    queryFn: () => ingredientService.listIngredients({ is_active: 'true', limit: 200, location_id: sourceLocationId || undefined }),
    staleTime: 15_000,
    enabled: isRestaurant,
  });
  // Unified catalog: { id, name, code (sku or unit), stock }
  const products = useMemo(() => {
    if (isRestaurant) {
      const q = productSearch.trim().toLowerCase();
      return (ingredientsData?.data ?? [])
        .filter((g) => !q || g.name.toLowerCase().includes(q))
        .map((g) => ({ id: g.id, name: g.name, sku: g.unit, stock_quantity: g.stock_quantity }));
    }
    return (productsData?.data ?? []).map((p) => ({ id: p.id, name: p.name, sku: p.sku, stock_quantity: p.stock_quantity }));
  }, [isRestaurant, ingredientsData, productsData, productSearch]);

  const selectedProductIds = useMemo(
    () => new Set(items.map((i) => i.product_id).filter(Boolean)),
    [items],
  );
  const productOptions = useMemo(
    () =>
      products
        .filter((p) => !selectedProductIds.has(p.id))
        .map((p) => ({
          value: p.id,
          label: `${p.name} (${p.sku}) \u2014 Stock: ${p.stock_quantity ?? 0}`,
        })),
    [products, selectedProductIds],
  );

  const sourceOptions = useMemo(
    () => locations.map((l) => ({ value: l.id, label: l.name })),
    [locations],
  );
  const destinationOptions = useMemo(
    () =>
      (destinationsData ?? [])
        .filter((l) => l.id !== sourceLocationId)
        .map((l) => ({ value: l.id, label: l.name })),
    [destinationsData, sourceLocationId],
  );

  function handleSourceChange(locId: string) {
    setSourceLocationId(locId);
    if (locId !== sourceLocationId) {
      setItems([
        { key: generateKey(), product_id: '', product_name: '', product_sku: '', available_stock: 0, quantity: '' },
      ]);
      if (destinationLocationId === locId) {
        setDestinationLocationId('');
      }
    }
  }

  function addItemRow() {
    setItems((prev) => [
      ...prev,
      { key: generateKey(), product_id: '', product_name: '', product_sku: '', available_stock: 0, quantity: '' },
    ]);
  }

  function removeItemRow(key: string) {
    setItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((i) => i.key !== key);
    });
  }

  function updateItem(key: string, field: keyof ItemRow, value: string | number) {
    setItems((prev) =>
      prev.map((i) => {
        if (i.key !== key) return i;
        const updated = { ...i, [field]: value };
        if (field === 'product_id') {
          const product = products.find((p) => p.id === value);
          if (product) {
            updated.product_name = product.name;
            updated.product_sku = product.sku;
            updated.available_stock = Number(product.stock_quantity ?? 0);
          }
        }
        return updated;
      }),
    );
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!sourceLocationId) errs.source = 'Source location is required';
    if (!destinationLocationId) errs.destination = 'Destination location is required';
    if (sourceLocationId && destinationLocationId && sourceLocationId === destinationLocationId) {
      errs.destination = 'Source and destination must be different';
    }

    const validItems = items.filter((i) => i.product_id);
    if (validItems.length === 0) {
      errs.items = 'Add at least one item';
    }
    for (let idx = 0; idx < validItems.length; idx++) {
      const item = validItems[idx];
      const qty = parseFloat(item.quantity);
      if (!qty || qty <= 0) {
        errs[`item_${idx}_qty`] = 'Quantity must be > 0';
      } else if (qty > item.available_stock) {
        errs[`item_${idx}_qty`] = `Exceeds available stock (${item.available_stock})`;
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const handleSubmit = useCallback(() => {
    if (!validate()) return;

    const validItems = items.filter((i) => i.product_id && parseFloat(i.quantity) > 0);
    const payload = {
      source_location_id: sourceLocationId,
      destination_location_id: destinationLocationId,
      items: validItems.map((i) => ({
        // Restaurant lines carry ingredient_id; retail lines carry product_id.
        ...(isRestaurant ? { ingredient_id: i.product_id } : { product_id: i.product_id }),
        quantity: parseFloat(i.quantity),
      })),
      notes: notes.trim() || undefined,
    };

    createMutation.mutate(payload, {
      onSuccess: (result) => {
        toast({ title: `Transfer ${result.transfer_number} created`, variant: 'success' });
        navigate('/inventory/stock-transfers');
      },
      onError: (err: Error) => {
        toast({ title: err.message, variant: 'error' });
      },
    });
  }, [sourceLocationId, destinationLocationId, items, notes, createMutation, navigate, toast]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/inventory/stock-transfers');
      }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, handleSubmit]);

  // Totals — drives the totals strip in the items card
  const lineCount = items.filter((i) => i.product_id).length;
  const totalQty = items
    .filter((i) => i.product_id)
    .reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0);

  return (
    <>
      {/* Gradient header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 rounded-2xl p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative">
          <h1 className="text-2xl font-bold tracking-tight text-white">New Stock Transfer</h1>
          <p className="text-teal-100 mt-1">Move inventory from one location to another</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6 animate-slide-up">
        {/* Transfer Route */}
        {/* relative z-20: lift above the Transfer Items card below so the
            Source/Destination location dropdowns (backdrop-blur stacking
            context) aren't painted over when open. */}
        <div className={`relative z-20 ${GLASS_FORM} p-6`}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">
            Transfer Route
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-start">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Source Location <span className="text-danger-500">*</span>
              </label>
              <Select
                options={sourceOptions}
                value={sourceLocationId}
                onChange={(v) => handleSourceChange(typeof v === 'string' ? v : '')}
                placeholder="Select source location"
              />
              {errors.source && (
                <p className="text-xs text-danger-600 mt-1">{errors.source}</p>
              )}
            </div>
            <div className="hidden sm:flex items-center justify-center pt-7">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-100 to-brand-100 dark:from-teal-900/30 dark:to-brand-900/30 flex items-center justify-center shadow-sm">
                <ArrowRightIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Destination Location <span className="text-danger-500">*</span>
              </label>
              <Select
                options={destinationOptions}
                value={destinationLocationId}
                onChange={(v) => setDestinationLocationId(typeof v === 'string' ? v : '')}
                placeholder="Select destination location"
                disabled={!sourceLocationId}
              />
              {errors.destination && (
                <p className="text-xs text-danger-600 mt-1">{errors.destination}</p>
              )}
            </div>
          </div>
        </div>

        {/* Transfer Items */}
        {/* relative z-10: keeps the per-line product dropdowns above the Notes
            card while still sitting below the Transfer Route card. */}
        <div className={`relative z-10 ${GLASS} p-6`}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Transfer Items
            </h3>
            <div className="w-56">
              <Input
                placeholder={isRestaurant ? 'Search ingredients...' : 'Search products...'}
                value={productSearch}
                onChange={(v) => setProductSearch(v)}
              />
            </div>
          </div>

          {errors.items && (
            <p className="text-xs text-danger-600 mb-3">{errors.items}</p>
          )}

          {!sourceLocationId && (
            <p className="text-sm text-neutral-400 dark:text-neutral-500 italic mb-4">
              Showing all products — pick a source location to scope the list to
              that location's stock.
            </p>
          )}

          {/* overflow-visible (not overflow-x-auto): overflow-x:auto forces
              overflow-y to compute to auto as well, which clips the open product
              dropdown (especially when it flips upward). The table has fixed
              column widths and fits the card, so horizontal scroll isn't needed. */}
          <div className="overflow-visible">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200/70 dark:border-neutral-700/70">
                  <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide w-2/5">{isRestaurant ? 'Ingredient' : 'Product'}</th>
                  <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide w-28">Available</th>
                  <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide w-32">Transfer Qty</th>
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const qtyNum = parseFloat(item.quantity) || 0;
                  const overstock = item.product_id && qtyNum > item.available_stock;
                  return (
                    <tr
                      key={item.key}
                      className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0 even:bg-neutral-50/40 dark:even:bg-neutral-800/20"
                    >
                      <td className="py-2.5 pr-3">
                        <Select
                          variant="searchable"
                          options={[
                            ...(item.product_id
                              ? [{ value: item.product_id, label: `${item.product_name} (${item.product_sku})` }]
                              : []),
                            ...productOptions,
                          ]}
                          value={item.product_id}
                          onChange={(v) => updateItem(item.key, 'product_id', typeof v === 'string' ? v : '')}
                          placeholder={isRestaurant ? 'Select ingredient' : 'Select product'}
                        />
                      </td>
                      <td className="py-2.5 pr-3 text-right">
                        <span className="text-sm text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
                          {item.product_id ? item.available_stock : '\u2014'}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3">
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(v) => updateItem(item.key, 'quantity', v)}
                          placeholder="0"
                          disabled={!item.product_id}
                          className={overstock ? 'border-danger-400 focus:border-danger-500 focus:ring-danger-500' : ''}
                        />
                        {errors[`item_${idx}_qty`] && (
                          <p className="text-xs text-danger-600 mt-0.5">{errors[`item_${idx}_qty`]}</p>
                        )}
                      </td>
                      <td className="py-2.5">
                        <button
                          type="button"
                          onClick={() => removeItemRow(item.key)}
                          disabled={items.length <= 1}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Remove item"
                          aria-label="Remove item"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <Button variant="secondary" onClick={addItemRow}>
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add Item
            </Button>
            <div className="flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
              <span className="tabular-nums font-mono">
                <span className="font-semibold text-neutral-700 dark:text-neutral-300">{lineCount}</span> line{lineCount === 1 ? '' : 's'}
              </span>
              <span className="tabular-nums font-mono">
                <span className="font-semibold text-neutral-700 dark:text-neutral-300">{totalQty}</span> total qty
              </span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className={`${GLASS_INTERACTIVE} p-6`}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
            Notes
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this transfer..."
            rows={3}
            className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
            maxLength={1000}
          />
          <p className="text-xs text-neutral-400 mt-1 text-right tabular-nums font-mono">{notes.length}/1000</p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between pb-6">
          <Button variant="secondary" onClick={() => navigate('/inventory/stock-transfers')}>
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Transfer'}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function StockTransferCreatePage() {
  return (
    <PageContainer
      title="New Stock Transfer"
      breadcrumbs={[
        { label: 'Inventory', href: '/inventory' },
        { label: 'Stock Transfers', href: '/inventory/stock-transfers' },
        { label: 'New' },
      ]}
    >
      <ErrorBoundary>
        <StockTransferCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
